import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import {
  currentUser, toAdminUser, onAuthChange,
  loginWithPassword, requestPasswordRecovery, acceptInvite,
  setPasswordFromRecovery, confirmSignup, consumeHashToken, authErrorMessage,
} from '../lib/auth';
import type { AdminUser } from '../types';

interface Props {
  children: (user: AdminUser) => ReactNode;
}

type Mode = 'login' | 'forgot' | 'invite' | 'recovery';

/**
 * Gates the app behind Netlify Identity. Renders a native, password-manager
 * friendly form (real <form>, real email/password inputs with autocomplete
 * hints) so Dashlane & co. can autofill — and with no third-party branding,
 * since we own every pixel. Also handles invite / recovery / confirmation
 * links that Netlify emails out.
 */
export function AuthGuard({ children }: Props) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [ready, setReady] = useState(false);

  const [mode, setMode] = useState<Mode>('login');
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Bootstrap: handle any email-link token, then sync the current session.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const tok = consumeHashToken();
      if (tok?.type === 'error') {
        setError(tok.message);
      } else if (tok?.type === 'invite') {
        setMode('invite'); setToken(tok.token);
      } else if (tok?.type === 'recovery') {
        setMode('recovery'); setToken(tok.token);
      } else if (tok?.type === 'confirm') {
        try { await confirmSignup(tok.token); if (!cancelled) setInfo('Email confirmed. You can sign in now.'); }
        catch (e) { if (!cancelled) setError(authErrorMessage(e)); }
      }
      if (!cancelled) {
        setUser(toAdminUser(currentUser()));
        setReady(true);
      }
    })();

    const unsubscribe = onAuthChange(() => setUser(toAdminUser(currentUser())));
    return () => { cancelled = true; unsubscribe(); };
  }, []);

  function switchMode(next: Mode) {
    setMode(next);
    setError(null); setInfo(null);
    setPassword(''); setConfirm('');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null); setInfo(null); setBusy(true);
    try {
      if (mode === 'login') {
        await loginWithPassword(email, password);
        setUser(toAdminUser(currentUser()));
      } else if (mode === 'forgot') {
        await requestPasswordRecovery(email);
        setInfo('If an account exists for that email, a reset link is on its way.');
      } else if (mode === 'invite' || mode === 'recovery') {
        if (password.length < 8) { setError('Use at least 8 characters.'); return; }
        if (password !== confirm) { setError('The two passwords do not match.'); return; }
        if (mode === 'invite') await acceptInvite(token, password);
        else await setPasswordFromRecovery(token, password);
        setUser(toAdminUser(currentUser()));
      }
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream-tint">
        <div className="text-ink-muted text-[13.5px]">Loading…</div>
      </div>
    );
  }

  if (user) return <>{children(user)}</>;

  const setPasswordMode = mode === 'invite' || mode === 'recovery';
  const copy = {
    login:    { title: 'Sign in',          hint: 'Restricted access. Use the email tied to your Netlify Identity invitation.', cta: 'Sign in', busy: 'Signing in…' },
    forgot:   { title: 'Reset password',   hint: 'Enter your email and we will send you a link to set a new password.',       cta: 'Send reset link', busy: 'Sending…' },
    invite:   { title: 'Set your password', hint: 'Welcome. Choose a password to activate your admin account.',               cta: 'Activate account', busy: 'Activating…' },
    recovery: { title: 'Choose a new password', hint: 'Pick a new password for your admin account.',                          cta: 'Update password', busy: 'Updating…' },
  }[mode];

  const inputCls =
    'w-full h-10 px-3 rounded-md border border-line bg-white text-[14px] text-ink ' +
    'placeholder:text-ink-faint ring-input transition-shadow motion-reduce:transition-none';

  return (
    <div className="min-h-screen grid place-items-center bg-cream-tint p-6 sm:p-8">
      <div className="bg-white rounded-[12px] shadow-pop border border-line w-full max-w-[420px] p-7 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-navy text-white grid place-items-center font-display text-[16px]">F</div>
          <div>
            <div className="h-display text-[18px] leading-tight">Finance for Quantum</div>
            <div className="text-[12.5px] text-ink-muted">Admin console</div>
          </div>
        </div>

        <h1 className="h-display text-[20px] mb-1.5">{copy.title}</h1>
        <p className="text-[13px] text-ink-muted mb-5 leading-relaxed">{copy.hint}</p>

        <form onSubmit={handleSubmit} className="space-y-3.5" aria-busy={busy}>
          {!setPasswordMode && (
            <div>
              <label htmlFor="email" className="block text-[12px] font-medium text-ink-muted mb-1.5">Email</label>
              <input
                id="email" name="email" type="email" autoComplete="username"
                inputMode="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" className={inputCls}
              />
            </div>
          )}

          {mode === 'login' && (
            <div>
              <label htmlFor="password" className="block text-[12px] font-medium text-ink-muted mb-1.5">Password</label>
              <input
                id="password" name="password" type="password" autoComplete="current-password"
                required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className={inputCls}
              />
            </div>
          )}

          {setPasswordMode && (
            <>
              <div>
                <label htmlFor="new-password" className="block text-[12px] font-medium text-ink-muted mb-1.5">New password</label>
                <input
                  id="new-password" name="new-password" type="password" autoComplete="new-password"
                  required autoFocus minLength={8}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters" className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="confirm-password" className="block text-[12px] font-medium text-ink-muted mb-1.5">Confirm password</label>
                <input
                  id="confirm-password" name="confirm-password" type="password" autoComplete="new-password"
                  required minLength={8}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter password" className={inputCls}
                />
              </div>
            </>
          )}

          {error && (
            <p role="alert" className="text-[12.5px] text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2 leading-snug">
              {error}
            </p>
          )}
          {info && (
            <p role="status" className="text-[12.5px] text-funds-deep bg-funds-soft border border-funds/20 rounded-md px-3 py-2 leading-snug">
              {info}
            </p>
          )}

          <button
            type="submit" disabled={busy}
            className="w-full h-10 rounded-md bg-navy text-white text-[13.5px] font-medium hover:bg-navy-soft disabled:opacity-60 disabled:cursor-not-allowed transition-colors motion-reduce:transition-none"
          >
            {busy ? copy.busy : copy.cta}
          </button>
        </form>

        {mode === 'login' && (
          <button
            onClick={() => switchMode('forgot')}
            className="mt-4 text-[12.5px] text-ink-muted hover:text-ink underline-offset-2 hover:underline transition-colors motion-reduce:transition-none"
          >
            Forgot password?
          </button>
        )}
        {mode === 'forgot' && (
          <button
            onClick={() => switchMode('login')}
            className="mt-4 text-[12.5px] text-ink-muted hover:text-ink underline-offset-2 hover:underline transition-colors motion-reduce:transition-none"
          >
            ← Back to sign in
          </button>
        )}
      </div>
    </div>
  );
}
